import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Provider } from '@angular/core';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep, isNil } from 'lodash';

import { FormGroupControlValueAccessor } from '../../../generic/abstract-classes/form-group-control-value-accessor';
import { CommunicationWorkflowConfigEditorFormType } from '../editor-output/communication-workflow-config-editor-form-type';
import { CommunicationWorkflowConfigEditorOutput } from '../editor-output/communication-workflow-config-editor-output';

const VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CallListCommunicationWorkflowConfigEditorComponent),
  multi: true,
};
const VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => CallListCommunicationWorkflowConfigEditorComponent),
  multi: true,
};

@UntilDestroy()
@Component({
  selector: 'app-call-list-communication-workflow-config-editor',
  templateUrl: './call-list-communication-workflow-config-editor.component.html',
  styleUrls: ['./call-list-communication-workflow-config-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [VALUE_ACCESSOR, VALIDATOR],
})
export class CallListCommunicationWorkflowConfigEditorComponent extends FormGroupControlValueAccessor<
  CommunicationWorkflowConfigEditorOutput,
  CommunicationWorkflowConfigEditorOutput
> {
  public baseObj: CommunicationWorkflowConfigEditorOutput | undefined = undefined;

  public callFlowTypeOptions = [
    { label: 'callFlowStandardLabel', value: 'standard' },
    { label: 'callFlowAdvancedLabel', value: 'advanced' },
  ];

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////
  constructor(public changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Controls
  /////////////////////////////////////////////////////////////////////////////////////////////

  public get callFlowType() {
    return this.form.get('callFlowType') as UntypedFormControl;
  }

  public get communicationWorkflowId() {
    return this.form.get('communicationWorkflowId') as UntypedFormControl;
  }

  public get isCallFlowTypeAdvanced() {
    return this.callFlowType.value === 'advanced';
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Define Form
  /////////////////////////////////////////////////////////////////////////////////////////////
  public defineForm() {
    const formConfig = {} as any;
    formConfig.callFlowType = new UntypedFormControl('standard');
    formConfig.communicationWorkflowId = new UntypedFormControl('', []);
    this.form = new UntypedFormGroup(formConfig);

    this.callFlowType.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      const validators = this.isCallFlowTypeAdvanced ? [Validators.required] : [];
      this.communicationWorkflowId.setValidators(validators);
      this.communicationWorkflowId.updateValueAndValidity();
    });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Parse Value Change
  /////////////////////////////////////////////////////////////////////////////////////////////
  public parseValueChange(value: CommunicationWorkflowConfigEditorFormType) {
    return value;
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Write value
  /////////////////////////////////////////////////////////////////////////////////////////////

  public writeValue(value: CommunicationWorkflowConfigEditorOutput | undefined | null) {
    /**
     * Preserve any properties of the input, if it is specified (such as the id)
     */
    if (isNil(value)) return;

    this.baseObj = cloneDeep(value);

    const formValue: CommunicationWorkflowConfigEditorFormType = {
      communicationWorkflowId: this.baseObj?.communicationWorkflowId,
      callFlowType: this.baseObj?.communicationWorkflowId ? 'advanced' : 'standard',
    };
    this.form.patchValue(formValue, { emitEvent: true });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Validation Errors
  /////////////////////////////////////////////////////////////////////////////////////////////
  public makeValidationErrors(): ValidationErrors {
    return {
      'call-list-communication-workflow-editor': this.form.value,
    };
  }
}
